/**
 * Live Trading V3 — Spacebar Pull All, scoped to the FOCUSED GAME.
 *
 * One keyboard action that removes the trader's resting Kalshi exposure on
 * the game they are aimed at — and nothing else. The dangerous half of that
 * sentence is "and nothing else", so this module deliberately writes no new
 * cancellation logic at all. It is a keyboard entry point into the exact
 * machinery every mouse sweep already uses:
 *
 *   selectExactGameOrders  — the #272 exact-game selection: an order is a
 *     target only when its market resolves through marketStore's fail-closed
 *     canonical-key resolver to the SAME five-part game key. Reads the
 *     authoritative open-order state, never DOM rows, so off-screen and
 *     collapsed orders are included by construction. Anything that ties to
 *     the matchup but cannot be assigned to the exact game BLOCKS the whole
 *     action — cancelling "the easy subset" silently leaves exposure the
 *     trader believes is gone.
 *   freezeCancelSnapshot   — the immutable set the DELETEs are sent from.
 *   executeBulkOrderCancel — the ONE mixed-venue executor, with the #241
 *     cancel fence, per-order results, and retry-failed-only semantics.
 *
 * Boundaries in this release:
 *   • KALSHI ONLY — the hotkey lane is Kalshi-only everywhere else, and the
 *     pull is no wider than the keys that build the exposure. Polymarket
 *     resting orders on the game are NOT touched, and the summary does not
 *     pretend otherwise (the mouse "Cancel this game" sweep remains the
 *     mixed-venue tool).
 *   • Resting orders only. Positions are never flattened.
 *   • One pull at a time: a second Spacebar while one is pending refuses
 *     rather than duplicating cancellation traffic.
 *   • Every pull is AUTHORIZED server-side before any cancel is sent: the
 *     live_pull_all attempt record (game scope, exact frozen targets) must
 *     succeed first, and the relay refuses it when the LT3_PULL_ALL kill
 *     switch is off or the trader's LT2 grant is revoked. A failed or
 *     timed-out authorization refuses the WHOLE pull — fail closed, unlike
 *     the mouse sweeps' best-effort breadcrumb — because this is the
 *     packet's separate global kill boundary for the one-key action.
 */
import { useAccountStore } from '@/stores/accountStore';
import {
  selectExactGameOrders,
  freezeCancelSnapshot,
} from '@/lib/trading/gameOrderSelection';
import { executeBulkOrderCancel } from '@/lib/trading/bulkOrderCancel';
import { recordPullAllAttempt } from '@/lib/trading/bulkCancelAttemptAudit';
import type { GameRow } from '@/views/ValueDashboard/types';

export interface PullAllOutcome {
  status: 'refused' | 'empty' | 'done';
  /** One ack-line sentence — the strip is the trader's only receipt. */
  message: string;
  requested: number;
  submitted: number;
  failed: number;
}

let _pullInFlight = false;

export function isPullAllPending(): boolean {
  return _pullInFlight;
}

export function _resetPullAllForTests(): void {
  _pullInFlight = false;
}

export async function executeFocusedGamePull(game: GameRow): Promise<PullAllOutcome> {
  if (_pullInFlight) {
    return {
      status: 'refused',
      message: 'Pull All already pending — wait for the result',
      requested: 0,
      submitted: 0,
      failed: 0,
    };
  }

  const account = useAccountStore.getState();
  const selection = selectExactGameOrders({
    game,
    kalshiOrders: account.openOrders ?? [],
    // Kalshi-only in this release — see the module contract above.
    polyOrders: [],
  });

  if (selection.blockers.length > 0) {
    // Fail closed on the WHOLE action: a partially attributable game must
    // not be swept partially. The blocker messages name the orders.
    return {
      status: 'refused',
      message: `Pull refused — ${selection.blockers.length} order(s) could not be safely assigned to ${selection.gameLabel}`,
      requested: 0,
      submitted: 0,
      failed: 0,
    };
  }

  if (selection.targets.length === 0) {
    return {
      status: 'empty',
      message: `No resting Kalshi orders on ${selection.gameLabel}`,
      requested: 0,
      submitted: 0,
      failed: 0,
    };
  }

  _pullInFlight = true;
  try {
    const snapshot = freezeCancelSnapshot({
      scope: 'game',
      label: selection.gameLabel,
      targets: selection.targets,
    });
    // SERVER AUTHORIZATION first — the kill boundary. Any failure (403 from
    // the kill switch or a revoked grant, a timeout, a network error)
    // refuses the whole pull before a single cancel is sent.
    try {
      await recordPullAllAttempt(snapshot);
    } catch (err) {
      return {
        status: 'refused',
        message: `Pull refused — ${
          err instanceof Error && err.message ? err.message : 'relay did not authorize the pull'
        }`,
        requested: 0,
        submitted: 0,
        failed: 0,
      };
    }
    const report = await executeBulkOrderCancel(snapshot, {
      cancelKalshiOrders: (ids) => account.cancelOrders([...ids], { refreshAfter: false }),
      // No Polymarket targets can exist in this snapshot (see above); the
      // executor still requires the dep, and reaching it would be a bug.
      requestPolymarketCancel: () => {
        throw new Error('Pull All is Kalshi-only');
      },
      refreshKalshi: () => account.refreshOrders(),
      refreshPolymarket: async () => {},
      // Authorization above already recorded the attempt.
    });

    const failed = report.failed.length + report.alreadyPending.length;
    const parts = [
      `pulled ${report.submitted.length}/${snapshot.targets.length}`,
      ...(report.failed.length > 0 ? [`${report.failed.length} FAILED`] : []),
      ...(report.alreadyPending.length > 0
        ? [`${report.alreadyPending.length} already cancelling`]
        : []),
      ...(report.refreshErrors.length > 0 ? ['refresh degraded'] : []),
      ...(report.auditError != null ? ['audit degraded'] : []),
    ];
    return {
      status: 'done',
      message: `${selection.gameLabel}: ${parts.join(', ')}`,
      requested: snapshot.targets.length,
      submitted: report.submitted.length,
      failed,
    };
  } finally {
    _pullInFlight = false;
  }
}
